import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import BaseHTMLView from 'Components/HTMLView';
import { Box, Flex, Image, Text } from 'Components/Base';
import { ClearLoadingIcon } from 'Components/Classroom/Clear/ClearAttachment/AttachmentItem';
import { EXAM_PAPER_ITEM_TYPE } from 'Components/Quiz/constant';

const HTMLView = styled((props) => (
  <BaseHTMLView loadingComponent={() => <ClearLoadingIcon />} {...props} />
))`
  font-size: 16px;
  p {
    margin-top: 8px;
    margin-bottom: 8px;
    img {
      width: 100%;
      object-fit: contain;
    }
  }
`;

const Divider = styled(Box)`
  border-bottom: 1px solid ${({ theme: { colors } }) => colors.greyscale.grey4};
  margin: 24px 0;
`;

const ProblemNoBoxRight = styled(Text)`
  border: 1px solid ${({ theme: { colors } }) => colors.greyscale.grey4};
  border-left: none;
`;

function ProblemNoBox({ problemNo }) {
  return (
    <Flex mb={6}>
      <Text textStyle={'body1Medium'} p={4} color="white" bg="black">
        ข้อ
      </Text>
      <ProblemNoBoxRight textStyle={'body1Medium'} pt={4} px={6}>
        {problemNo}
      </ProblemNoBoxRight>
    </Flex>
  );
}

function TextAnswerIndex({ indexLabel, ...restProps }) {
  return (
    <Text textStyle={'subtitle1'} mr={4} style={{ flexShrink: 0 }} {...restProps}>
      {indexLabel}
    </Text>
  );
}

function renderChoiceContent(answer) {
  return (
    <Flex flexDirection={'column'} mt={7} alignItems={'flex-start'}>
      <TextAnswerIndex indexLabel={`ข้อที่ ${answer.answerIndex}.`} />
      <HTMLView rawContent={answer.text} />
      {answer.image && <Image src={answer.image} mt={2} height={'140px'} />}
    </Flex>
  );
}

function renderMultipleChoice(answer) {
  return renderChoiceContent(answer);
}

function renderMultipleSelect(answers) {
  return answers.map((answer) => {
    return <Box key={answer.choiceId}>{renderChoiceContent(answer)}</Box>;
  });
}

function renderFillNumber(answer) {
  return (
    <Box my={6}>
      <HTMLView rawContent={answer.toString()} />
    </Box>
  );
}

function SolutionAnswerContent({ type, answer }) {
  if (!answer) return '-';

  switch (type) {
    case EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE:
    case EXAM_PAPER_ITEM_TYPE.MULTIPLE_CHOICE_DIFFERENT_SCORE:
      return renderMultipleChoice(answer);
    case EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT:
    case EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT_WITH_CORRECT_ANSWERS:
      return renderMultipleSelect(answer);
    case EXAM_PAPER_ITEM_TYPE.FILL_NUMBER:
      return renderFillNumber(answer);
    default:
      return '-';
  }
}

function SolutionContent(props) {
  const { problemNo, question, answer, solutionDetail, type, ...restProps } = props;
  const hasChoices = question.choices.length > 0;

  return (
    <Box mt={6} {...restProps}>
      <ProblemNoBox problemNo={problemNo} />
      <HTMLView rawContent={question.description || '-'} />
      {hasChoices &&
        question.choices.map((choice, index) => {
          const answerIndex = index + 1;
          const answer = { ...choice, answerIndex };
          return <Box key={choice.choiceId}>{renderChoiceContent(answer)}</Box>;
        })}
      <Divider />
      <Text textStyle={'subtitle1'}>คำตอบที่ถูกคือ</Text>
      <SolutionAnswerContent type={type} answer={answer} />
      <Divider />
      <Text textStyle={'subtitle1'} mb={4}>
        เฉลยละเอียด
      </Text>
      <HTMLView rawContent={solutionDetail || '-'} />
    </Box>
  );
}

SolutionContent.propTypes = {
  problemNo: PropTypes.number,
  question: PropTypes.object,
  answer: PropTypes.object,
  solutionDetail: PropTypes.string,
  type: PropTypes.string,
};

export default SolutionContent;

/**
 * @typedef {Object} SolutionContentProps
 * @property {number} problemNo
 * @property {object} question
 * @property {object} answer
 * @property {string} solutionDetail
 * @property {string} type
 */
